import { useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import { useTranslation } from "react-i18next";
import { DialogActions, DialogContent } from "~/common/mui/Dialog/Dialog";
import { Box, Button, Drawer, drawerClasses, IconButton, styled, Typography } from "~/common/mui";
import { useState } from "react";
import { type UnitMode } from "~/domains/userPreferences/types/UnitMode";
import { DisplayModeButtonList } from "~/features/search/components/common/SearchResultsDisplayMode/SearchResultsDisplayMode";
import { useSearchPageType } from "~/features/search/context/SearchPageTypeContext";
import { UnitModeToggle } from "~/domains/userPreferences/components/UnitModeToggle";
import { verticalStacker } from "~/styles/styledMixins";
import Close from "@mui/icons-material/Close";
import SettingsIcon from "@mui/icons-material/Settings";
import { type SearchDisplayMode } from "~/features/search/models/models";
import { SearchPageType } from "~/features/search/models/SearchPageType";
import { STICKY_TO_BOTTOM } from "~/common/components/icon/Elevation";
import { Chip } from "~/common/mui/Chip/Chip";

interface ViewChipProps {
  initialViewMode: SearchDisplayMode;
  initialUnit: UnitMode;
  applyValues: (unit: UnitMode, displayMode: SearchDisplayMode) => void;
  newDesign: boolean;
}

export const ViewChip = ({ initialViewMode, initialUnit, applyValues, newDesign }: ViewChipProps) => {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState(initialViewMode);
  const [unit, setUnit] = useState(initialUnit);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const isMinTablet = useIsMinTabletSize();

  const onClose = () => {
    setIsDrawerOpen(false);
  };

  const onApply = () => {
    applyValues(unit, viewMode);
    onClose();
  };

  const onCancel = () => {
    setViewMode(initialViewMode);
    setUnit(initialUnit);
    onClose();
  };

  const pageType = useSearchPageType();
  // TODO - other cases! Campaign page?

  const showTreeButton = pageType !== SearchPageType.Campaign;

  return (
    <>
      <Chip
        icon={<SettingsIcon />}
        variant="assist"
        label={t("page.search.chip.view")}
        onClick={() => setIsDrawerOpen(true)}
      />
      <StyledDrawer anchor={isMinTablet ? "left" : "bottom"} open={isDrawerOpen} onClose={() => setIsDrawerOpen(false)}>
        <DrawerContent sx={{ ...verticalStacker("24px") }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography
              variant="titleLarge"
              sx={{
                fontWeight: "bold",
              }}
            >
              Customize the view
            </Typography>
            <IconButton variant="contained" color="secondary" onClick={() => setIsDrawerOpen(false)}>
              <Close />
            </IconButton>
          </Box>

          <UnitModeToggle value={unit} onChange={setUnit} />

          {!newDesign && (
            <DisplayModeButtonList
              setDisplayMode={setViewMode}
              displayMode={viewMode}
              showSeriesTree={showTreeButton}
            />
          )}
        </DrawerContent>

        <DrawerActions>
          <Button variant="text" fullWidth onClick={onCancel}>
            Cancel
          </Button>

          <Button fullWidth onClick={onApply}>
            OK
          </Button>
        </DrawerActions>
      </StyledDrawer>
    </>
  );
};

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  [`.${drawerClasses.paper}`]: {
    [theme.breakpoints.down("tablet")]: {
      height: "70vh",
      maxHeight: "700px",
    },
    [theme.breakpoints.between("tablet", "desktop-s")]: {
      width: "45vw",
      maxWidth: "550px",
    },
  },
}));

const DrawerContent = styled(DialogContent)({
  padding: "16px",
});

const DrawerActions = styled(DialogActions)({
  padding: "8px 16px",
  boxShadow: STICKY_TO_BOTTOM,
});
